import "./modal.scss";
import { Modal as BootstrapModal } from 'bootstrap';

const DEFAULT_MODAL_TRIGGER_SELECTOR = '.m2-modal';
const DEFAULT_MODAL_CLOSE_SELECTOR = '.close-modal';

type ModalHook = () => void | Promise<void>;

type ModalOptions = {
    onClick?: ModalHook;
    onOpen?: ModalHook;
    onClose?: ModalHook;
    modalTitle?: string;
    modalSize?: string;
    modalTarget?: string;
    forceDialog?: boolean;
    backdrop?: boolean | 'static';
    keyboard?: boolean;
    headless?: boolean;
};

type ConfirmModalOptions = {
    title?: string;
    message: string;
    confirmText?: string;
    cancelText?: string;
    confirmButtonClass?: string;
    cancelButtonClass?: string;
    onConfirm?: () => void | Promise<void>;
    onCancel?: () => void | Promise<void>;
};

export class Modal {
    private static readonly instances = new Map<string, Modal>();
    private static readonly processedTriggers = new WeakSet<HTMLElement>();
    private readonly id: string;
    private bsModal: BootstrapModal | null = null;
    private modalElement: HTMLElement | null = null;
    private headless = false;
    private readonly contentElement: HTMLElement;
    private readonly originalParent: HTMLElement;

    private constructor(
        contentElement: HTMLElement,
        private options: ModalOptions = {}
    ) {
        this.id = contentElement.id || `modal-${crypto.randomUUID()}`;
        Modal.instances.set(this.id, this);
        this.contentElement = contentElement;
        this.originalParent = contentElement.parentElement!;
        this.headless = options.headless ?? false;
        this.createModal();
    }

    // Public Methods
    public static create(
        modalTarget: string | HTMLElement,
        options: ModalOptions = {}
    ): Modal | null {
        try {
            const contentElement =
                typeof modalTarget === 'string'
                    ? document.querySelector<HTMLElement>(
                        modalTarget.startsWith('#') ? modalTarget : `#${modalTarget}`
                      )
                    : modalTarget;

            if (!contentElement || !contentElement.parentElement) {
                return null;
            }

            const modal = new Modal(contentElement, options);
            modal.show();
            return modal;
            
        } catch (error) {
            console.error('Failed to create Modal instance:', error);
            return null;
        }
    }

    private static createConfirmModalContent(
        message: string,
        cancelButtonClass: string,
        cancelText: string,
        confirmButtonClass: string,
        confirmText: string
    ): HTMLElement {
        const contentElement = document.createElement('div');
        contentElement.className = 'confirm-modal-content';
        contentElement.innerHTML = `
            <div class="confirm-message">${message}</div>
            <div class="modal-tools">
                <button type="button" class="btn ${confirmButtonClass} confirm-button">
                    ${confirmText}
                </button>
                 <button type="button" class="btn secondary ${cancelButtonClass} cancel-button">
                    ${cancelText}
                </button>
            </div>
        `;
        return contentElement;
    }

    public static createAlertModal(modalData): Modal | null {
        const { title, message, closeBtnLabel } = modalData;
        try {
            const contentElement = document.createElement('div');
            contentElement.className = 'alert-modal-content';
            contentElement.innerHTML = `
                <div class="alert-message">${message}</div>
                <div class="modal-tools">
                    <button type="button" class="btn primary close-button" data-bs-dismiss="modal">
                        Close
                    </button>
                </div>
            `;

            document.body.appendChild(contentElement);

            const modal = new Modal(contentElement, {
                modalTitle: title,
                modalSize: 'modal-sm',
                backdrop: 'static',
                keyboard: true,
                onClose: async () => {
                    contentElement.remove();
                }
            });

            if (!modal) {
                contentElement.remove();
                return null;
            }

            modal.show();
            return modal;

        } catch (error) {
            console.error('Failed to create alert modal:', error);
            return null;
        }
    }

    public static createConfirmModal(options: ConfirmModalOptions): Modal | null {
        try {
            const {
                title = 'Confirm',
                message,
                confirmText = 'Confirm',
                cancelText = 'Cancel',
                confirmButtonClass = '',
                cancelButtonClass = '',
                onConfirm,
                onCancel
            } = options;

            const contentElement = Modal.createConfirmModalContent(
                message,
                cancelButtonClass,
                cancelText,
                confirmButtonClass,
                confirmText
            );

            document.body.appendChild(contentElement);

            const modal = new Modal(contentElement, {
                modalTitle: title,
                modalSize: 'modal-sm',
                backdrop: 'static',
                keyboard: true,
                onClose: async () => {
                    contentElement.remove();
                }
            });

            if (!modal) {
                contentElement.remove();
                return null;
            }

            const confirmButton = contentElement.querySelector<HTMLButtonElement>('.confirm-button');
            const cancelButton = contentElement.querySelector<HTMLButtonElement>('.cancel-button');

            confirmButton?.addEventListener('click', async () => {
                if (typeof onConfirm === 'function') {
                    await onConfirm();
                }
                modal.hide();
            });

            cancelButton?.addEventListener('click', async () => {
                if (typeof onCancel === 'function') {
                    await onCancel();
                }
                modal.hide();
            });

            modal.show();
            return modal;

        } catch (error) {
            console.error('Failed to create confirmation modal:', error);
            return null;
        }
    }

    public static setModalTriggers(
        triggerSelector: string = DEFAULT_MODAL_TRIGGER_SELECTOR,
        options: ModalOptions = {}
    ): void {
        try {

            const triggers = document.querySelectorAll<HTMLElement>(triggerSelector);

            if (!triggers.length) {
                return;
            }

            triggers.forEach(trigger => {
                if (!(trigger instanceof HTMLButtonElement || trigger instanceof HTMLAnchorElement)) return;
                if (Modal.processedTriggers.has(trigger)) return;
                Modal.processedTriggers.add(trigger);

                trigger.addEventListener('click', async (e) => {
                    e.preventDefault();
                    
                    const modalTitle = trigger.dataset.modalTitle ?? options.modalTitle;
                    const modalSize = trigger.dataset.modalSize ?? options.modalSize;
                    const contentId = trigger.dataset.modalTarget ?? options.modalTarget;

                    if (!contentId) {
                        console.warn('Modal: trigger missing data-modal-target', trigger);
                        return;
                    }

                    const triggerOptions: ModalOptions = { ...options };
                    if (modalTitle) triggerOptions.modalTitle = modalTitle;
                    if (modalSize) triggerOptions.modalSize = modalSize;

                    if (typeof options.onClick === 'function') {
                        await options.onClick();
                    }

                    const selector = contentId.startsWith('#') ? contentId : `#${contentId}`;
                    Modal.create(selector, triggerOptions);
                });
            });
        } catch (error) {
            console.error('Failed to set modal triggers:', error);
        }
    }

    public setOnClick(onClick?: ModalHook): void {
        this.options.onClick = onClick;
    }

    public setOnOpen(onOpen?: ModalHook): void {
        this.options.onOpen = onOpen;
    }

    public setOnClose(onClose?: ModalHook): void {
        this.options.onClose = onClose;
    }

    public static closeAllModals(): void {
        const modals = document.querySelectorAll('.modal.show');
        modals.forEach(modalEl => {
            const bsModal = BootstrapModal.getInstance(modalEl as HTMLElement);
            bsModal?.hide();
        });
    }

    private createModal(): void {
        const forceDialog = (this.options.forceDialog ?? false);
        if (forceDialog) this.options.backdrop = 'static';
        
        this.modalElement = document.createElement('div');
        this.modalElement.className = 'modal fade';
        this.modalElement.setAttribute('tabindex', '-1');
        this.modalElement.innerHTML = `
            <div class="modal-dialog ${this.options.modalSize ?? ''} ${this.options.headless ? ' headless' : ''}">
                <div class="modal-content">
                    ${this.headless ? `
                        ${!forceDialog ? `
                        <button type="button" class="modal-close" data-bs-dismiss="modal" aria-label="Close">
                            <i class="fa-solid fa-xmark"></i>
                        </button>
                     ` : ''}
                    ` : `
                    <div class="modal-header">
                        ${this.options.modalTitle ? `<h5 class="modal-title">${this.options.modalTitle}</h5>` : ''}
                        ${!forceDialog ? `
                            <button type="button" class="modal-close" data-bs-dismiss="modal" aria-label="Close">
                                <i class="fa-solid fa-xmark"></i>
                            </button>
                        ` : ''}
                    </div>
                    `}
                    <div class="modal-body"></div>
                </div>
            </div>
        `;

        document.body.appendChild(this.modalElement);

        const modalBody = this.modalElement.querySelector('.modal-body');
        if (modalBody) {
            this.contentElement.classList.remove('hidden');
            this.contentElement.removeAttribute('hidden');
            modalBody.appendChild(this.contentElement);
        }

        const closeButtons = this.modalElement.querySelectorAll(DEFAULT_MODAL_CLOSE_SELECTOR);
        closeButtons.forEach(btn => {
            btn.addEventListener('click', () => this.hide());
        });

        this.bsModal = new BootstrapModal(this.modalElement, {
            backdrop: this.options.backdrop ?? true,
            keyboard: this.options.keyboard ?? true
        });

        this.modalElement.addEventListener('show.bs.modal', async () => {
            if (typeof this.options.onOpen === 'function') {
                await this.options.onOpen();
            }
        });

        this.modalElement.addEventListener('shown.bs.modal', () => {
            // Focus the first focusable element after modal is fully shown
            const firstFocusable = this.modalElement?.querySelector<HTMLElement>(
                'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
            );
            firstFocusable?.focus();
        });

        this.modalElement.addEventListener('hidden.bs.modal', async () => {
            if (typeof this.options.onClose === 'function') {
                await this.options.onClose();
            }
            this.destroy();
        });
    }

    public show(): void {
        this.bsModal?.show();
    }

    public hide(): void {
        this.bsModal?.hide();
    }

    public destroy(): void {
        this.bsModal?.dispose();
        this.modalElement?.remove();
        this.restoreContent();
        Modal.instances.delete(this.id);
    }

    private restoreContent(): void {
        if (!this.contentElement || !this.originalParent) return;
        this.contentElement.classList.add('hidden');
        this.originalParent.appendChild(this.contentElement);
    }

    public static getModal(id: string): Modal | null {
        return Modal.instances.get(id) ?? null;
    }
}